import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { ResourceLocation } from "../../../Constants/resourceLocations";
import { ReviewService } from "../../../Services/ReviewService";
import { resolveApiErrorMessage } from "../../../Util/apiErrorUtil";
import "./ReviewWidget.scss";

const THUMBS_RATING = {
    DOWN: 0,
    UP: 1,
} as const;

export interface ReviewWidgetProps {
    location: ResourceLocation;
    locationIdentifier: string;
    ratingScale?: "THUMBS";
    /** The user's own earlier rating and feedback — they come with the page's review summary, so the widget
     *  shows them back without a request of its own. */
    ownRatingValue?: number | null;
    ownFeedbackText?: string | null;
    onChanged?: () => void;
    /** The visitor is done: a thumbs-up was saved, or the feedback after a thumbs-down was sent. A page that
     *  tucks the widget away once somebody has answered does so here — not on {@code onChanged}, which
     *  also fires for the thumbs-down that is about to ask for feedback. */
    onCompleted?: () => void;
}

const ReviewWidget: React.FC<ReviewWidgetProps> = ({
    location,
    locationIdentifier,
    ratingScale = "THUMBS",
    ownRatingValue,
    ownFeedbackText,
    onChanged,
    onCompleted,
}) => {
    const { t } = useTranslation();
    const reviewService = useMemo(() => new ReviewService(), []);
    const [selected, setSelected] = useState<number | null>(ownRatingValue ?? null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackText, setFeedbackText] = useState(ownFeedbackText ?? "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setSelected(ownRatingValue ?? null);
    }, [ownRatingValue]);

    // Never while the box is open: a summary that reloads must not replace what is being typed.
    useEffect(() => {
        if (!showFeedback) {
            setFeedbackText(ownFeedbackText ?? "");
        }
    }, [ownFeedbackText, showFeedback]);

    const save = async (value: number): Promise<boolean> => {
        setSaving(true);
        try {
            await reviewService.submitReview(location, locationIdentifier, ratingScale, value, feedbackText || undefined);
            onChanged?.();
            return true;
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "review.saveError", t));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const submit = async (value: number) => {
        const previous = selected;
        setSelected(value);
        if (!(await save(value))) {
            setSelected(previous);
            return;
        }
        setShowFeedback(value === THUMBS_RATING.DOWN);
        if (value !== THUMBS_RATING.DOWN) {
            onCompleted?.();
        }
    };

    const submitFeedback = async () => {
        if (selected !== null && (await save(selected))) {
            setShowFeedback(false);
            onCompleted?.();
        }
    };

    if (ratingScale !== "THUMBS") {
        return null;
    }

    return (
        <div className="review-widget">
            <h3 className="review-widget__title">
                <i className="pi pi-comments" aria-hidden="true" />
                {t("review.wasThisHelpful")}
            </h3>
            <div className="review-widget__buttons">
                <button
                    type="button"
                    className={`review-widget__thumb review-widget__thumb--up${selected === THUMBS_RATING.UP ? " review-widget__thumb--selected" : ""}`}
                    disabled={saving}
                    aria-pressed={selected === THUMBS_RATING.UP}
                    onClick={() => submit(THUMBS_RATING.UP)}
                >
                    <i className={selected === THUMBS_RATING.UP ? "pi pi-thumbs-up-fill" : "pi pi-thumbs-up"} aria-hidden="true" />
                    {t("review.helpful")}
                </button>
                <button
                    type="button"
                    className={`review-widget__thumb review-widget__thumb--down${selected === THUMBS_RATING.DOWN ? " review-widget__thumb--selected" : ""}`}
                    disabled={saving}
                    aria-pressed={selected === THUMBS_RATING.DOWN}
                    onClick={() => submit(THUMBS_RATING.DOWN)}
                >
                    <i className={selected === THUMBS_RATING.DOWN ? "pi pi-thumbs-down-fill" : "pi pi-thumbs-down"} aria-hidden="true" />
                    {t("review.notHelpful")}
                </button>
            </div>
            {showFeedback && (
                <div className="review-widget__feedback">
                    <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={t("review.feedbackPlaceholder") as string}
                    />
                    <Button
                        type="button"
                        className="review-widget__submit"
                        label={t("review.sendFeedback") as string}
                        disabled={saving}
                        onClick={submitFeedback}
                    />
                </div>
            )}
        </div>
    );
};

export default ReviewWidget;
